// TK-15 — one task list, read in two windows. The float and the popped-out
// /widget/tasks page share this hook and TaskListBody, so neither can drift
// from the other. The routes are the task routes; nothing new was added.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { parseCapture } from '../../utils/captureParse';
import { quadrantOf } from '../../utils/quadrant';
import { todayLocal } from '../../utils/scheduleWeeks';
import { parseDay } from '../../utils/workingDays';
import {
  qRank, mineOf, handedOutOf, openOf, lateOf, todayListOf, doneTodayOf,
  dayKeyOf, comingUpOf,
} from '../../utils/taskLists';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CHANNEL = 'keymade-tasks';

const channel = () => {
  try { return new BroadcastChannel(CHANNEL); } catch { return null; }
};

// A change in either window tells the other at once; the in-page event stays
// for everything that already listened for it.
export const announceTasks = () => {
  window.dispatchEvent(new CustomEvent('tasks-changed'));
  const ch = channel();
  if (!ch) return;
  try { ch.postMessage({ kind: 'tasks-changed' }); } finally { ch.close(); }
};

export const useTaskList = ({ me, active = true, fast = false }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [openSteps, setOpenSteps] = useState({});
  const [dialog, setDialog] = useState(null); // { task } or null

  const today = useMemo(() => todayLocal(), []);

  const load = useCallback(async () => {
    const [t, u, c, p] = await Promise.all([
      axios.get(`${API}/tasks`, { params: { kind: 'todo' } }).catch(() => ({ data: [] })),
      axios.get(`${API}/users`).catch(() => ({ data: { users: [] } })),
      axios.get(`${API}/contacts/list`).catch(() => ({ data: [] })),
      axios.get(`${API}/projects`).catch(() => ({ data: [] })),
    ]);
    setTasks(Array.isArray(t.data) ? t.data : (t.data?.tasks || []));
    setUsers((u.data?.users || []).filter(x => x && x.id));
    const raw = Array.isArray(c.data) ? c.data : (c.data?.contacts || []);
    setContacts(raw.map(x => ({ id: x.id, name: `${x.first_name || ''} ${x.last_name || ''}`.trim() || x.company || x.email || 'Contact', company: x.company })));
    setProjects(Array.isArray(p.data) ? p.data : (p.data?.projects || []));
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    load();
    const again = () => load();
    const ch = channel();
    if (ch) ch.onmessage = again;
    window.addEventListener('tasks-changed', again);
    window.addEventListener('focus', again);
    // The channel carries a change the moment it happens. Without one, a
    // window that is sharing the list asks every five seconds instead.
    const id = setInterval(again, (fast && !ch) ? 5000 : 60000);
    return () => {
      window.removeEventListener('tasks-changed', again);
      window.removeEventListener('focus', again);
      if (ch) { ch.onmessage = null; ch.close(); }
      clearInterval(id);
    };
  }, [active, fast, load]);

  const todayKey = dayKeyOf(today);
  const plan = todayListOf(tasks, me, today);
  const doneToday = doneTodayOf(tasks, today);
  const three = [...plan, ...doneToday].filter(t => t.three_for === todayKey);
  const rest = plan.filter(t => t.three_for !== todayKey)
    .sort((a, b) => (lateOf(b, today) - lateOf(a, today)) || (qRank(quadrantOf(a, me)) - qRank(quadrantOf(b, me))));
  const restDone = doneToday.filter(t => t.three_for !== todayKey);
  const inbox = tasks.filter(t => openOf(t) && t.inbox).length;
  const coming = new Set(comingUpOf(tasks, me, today).map(t => t.id));
  const weekEnd = new Date(today.getTime() + 7 * 864e5);
  const dueSoon = tasks.filter(t => {
    if (coming.has(t.id) || !openOf(t) || t.inbox) return false;
    if (!(mineOf(t, me) || handedOutOf(t, me))) return false;
    const d = parseDay(t.due_date);
    return !!d && d > today && d <= weekEnd;
  }).length;

  const after = async (fn) => {
    try { await fn(); await load(); announceTasks(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  const toggle = (t) => after(() => axios.put(`${API}/tasks/${t.id}`, t.status === 'completed'
    ? { status: 'todo', clear: ['completed_at'] } : { status: 'completed' }));
  const stepToggle = (t, s) => after(() => axios.put(`${API}/tasks/${t.id}`, {
    subtasks: (t.subtasks || []).map(x => (x.id === s.id
      ? { ...x, completed: !x.completed, completed_at: x.completed ? null : new Date().toISOString() } : x)),
  }));
  const capture = async () => {
    const parsed = parseCapture(text, projects);
    if (!parsed.title) return;
    setBusy(true);
    await after(async () => {
      await axios.post(`${API}/tasks`, {
        title: parsed.title, kind: 'todo', status: 'todo', priority: 'medium',
        project_id: parsed.project_id, due_date: parsed.due_date, inbox: true, created_by: me,
      });
      setText('');
    });
    setBusy(false);
  };

  const doneCount = three.filter(t => t.status === 'completed').length + restDone.length;

  return {
    me, tasks, users, contacts, projects, load,
    text, setText, busy, openSteps, setOpenSteps, dialog, setDialog,
    three, rest, restDone, plan, inbox, dueSoon, doneCount,
    toggle, stepToggle, capture,
  };
};
